import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * HelpModal - Modal dialog for detailed help instructions
 * @param {string|JSX} content - Detailed help content (can be string or JSX)
 * @param {function} onClose - Callback to close the modal
 */
export default function HelpModal({ content, onClose }) {
  const { t } = useTranslation();
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Render content - if string, render as text with line breaks preserved
  const renderContent = () => {
    if (typeof content === 'string') {
      return (
        <div style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {content}
        </div>
      );
    }
    return content;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10002,
        padding: 20,
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 32,
          maxWidth: 600,
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 4px 32px rgba(30,41,59,0.18)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'transparent',
            border: 'none',
            fontSize: 24,
            color: '#64748b',
            cursor: 'pointer',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f1f5f9';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          aria-label={t('help.close')}
        >
          ×
        </button>

        <div
          style={{
            color: '#1f2937',
            fontSize: 16,
            lineHeight: 1.6,
          }}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

